import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const RoyaltyCalculator = () => {
  const [royaltyData, setRoyaltyData] = useState({
    streams: '',
    platform: 'Spotify',
    customRoyalty: '',
    showDetailedAnalysis: false
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRoyaltyData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setRoyaltyData(prev => ({
      ...prev,
      [name]: checked
    }));
    setError('');
  };

  const handlePlatformChange = (e) => {
    setRoyaltyData(prev => ({
      ...prev,
      platform: e.target.value
    }));
    setError('');
  };

  const calculateRoyalties = () => {
    const { streams, platform, customRoyalty } = royaltyData;

    if (!streams) {
      setError('Please enter the number of streams');
      return;
    }

    try {
      const streamCount = parseInt(streams);
      if (isNaN(streamCount) || streamCount <= 0) {
        setError('Please enter a valid number of streams');
        return;
      }

      // Default royalty rates per stream (in USD)
      const platformRates = {
        'Spotify': 0.0033,
        'Apple Music': 0.0014,
        'YouTube': 0.0006,
        'Amazon Music': 0.0011,
        'Pandora': 0.0017,
        'Custom': customRoyalty ? parseFloat(customRoyalty) : 0
      };

      if (platform === 'Custom' && !customRoyalty) {
        setError('Please enter a custom royalty rate');
        return;
      }

      const rate = platformRates[platform];
      const totalEarnings = streamCount * rate;
      const earningsPer1000 = rate * 1000;

      const generateChartData = () => {
        // Calculate earnings for comparison across platforms
        const comparisonData = Object.entries(platformRates).map(([platformName, rate]) => {
          return {
            platform: platformName,
            earnings: streamCount * rate
          };
        });

        return {
          labels: comparisonData.map(item => item.platform),
          datasets: [
            {
              label: 'Estimated Earnings',
              data: comparisonData.map(item => item.earnings),
              backgroundColor: [
                'rgba(255, 99, 132, 0.5)',
                'rgba(54, 162, 235, 0.5)',
                'rgba(255, 206, 86, 0.5)',
                'rgba(75, 192, 192, 0.5)',
                'rgba(153, 102, 255, 0.5)',
                'rgba(255, 159, 64, 0.5)'
              ],
              borderColor: [
                'rgba(255, 99, 132, 1)',
                'rgba(54, 162, 235, 1)',
                'rgba(255, 206, 86, 1)',
                'rgba(75, 192, 192, 1)',
                'rgba(153, 102, 255, 1)',
                'rgba(255, 159, 64, 1)'
              ],
              borderWidth: 1
            }
          ]
        };
      };

      setResults({
        platform,
        streams: streamCount,
        rate: rate.toFixed(4),
        totalEarnings: totalEarnings.toFixed(2),
        earningsPer1000: earningsPer1000.toFixed(2)
      });
      setChartData(generateChartData());

    } catch (error) {
      setError('Please enter valid numbers for all fields');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Streaming Royalties Calculator | Music Earnings Estimator</title>
        <meta name="description" content="Calculate your streaming service royalties and estimate earnings across different platforms. Get detailed financial analysis for your music streams." />
        <meta name="keywords" content="streaming royalties calculator, music earnings, Spotify calculator, Apple Music calculator, YouTube royalties" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">🎵</div>
            <h1 className="text-3xl font-bold text-gray-800">Streaming Services' Royalties Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>This calculator helps you estimate your streaming royalties and provides:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>✓ Earnings estimation across multiple platforms</li>
              <li>✓ Comparison between streaming services</li>
              <li>✓ Detailed financial breakdown</li>
              <li>✓ Custom royalty rate option</li>
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Streams:</label>
                <input
                  type="number"
                  id="streams"
                  name="streams"
                  value={royaltyData.streams}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter number of streams"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Streaming Platform:</label>
                <select
                  id="platform"
                  name="platform"
                  value={royaltyData.platform}
                  onChange={handlePlatformChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Spotify">Spotify</option>
                  <option value="Apple Music">Apple Music</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Amazon Music">Amazon Music</option>
                  <option value="Pandora">Pandora</option>
                  <option value="Custom">Custom Rate</option>
                </select>
              </div>

              {royaltyData.platform === 'Custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Royalty Rate per Stream (USD):</label>
                  <input
                    type="number"
                    step="0.0001"
                    id="customRoyalty"
                    name="customRoyalty"
                    value={royaltyData.customRoyalty}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter custom royalty rate"
                  />
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="showDetailedAnalysis"
                  checked={royaltyData.showDetailedAnalysis}
                  onChange={handleCheckboxChange}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Show detailed analysis and platform comparison
                </label>
              </div>
            </div>

            <button
              onClick={calculateRoyalties}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Royalties
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Royalty Calculation Results</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Estimated Earnings</h3>
                    <p className="text-gray-600">
                      Platform: {results.platform}<br />
                      Streams: {results.streams.toLocaleString()}<br />
                      Royalty Rate: ${results.rate}/stream<br />
                      Total Earnings: ${results.totalEarnings}<br />
                      Earnings per 1000 Streams: ${results.earningsPer1000}
                    </p>
                  </div>

                  {royaltyData.showDetailedAnalysis && (
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Platform Comparison</h3>
                      <p className="text-gray-600">
                        This comparison shows estimated earnings across different streaming platforms for the same number of streams.
                      </p>
                    </div>
                  )}
                </div>

                {chartData && royaltyData.showDetailedAnalysis && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Estimated Earnings (USD)'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Streaming Platform'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Earnings Comparison Across Streaming Platforms',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Financial Summary</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Total Streams:</span>
                      {results.streams.toLocaleString()}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Earnings:</span>
                      ${results.totalEarnings}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Earnings per 1000 Streams:</span>
                      ${results.earningsPer1000}
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Royalty Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to estimate your streaming royalties:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Stream Count</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the number of streams your track has received</li>
                  <li>Use approximate numbers if exact figures aren't available</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Select Platform</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose the streaming platform you want to analyze</li>
                  <li>Select "Custom Rate" if you have specific royalty information</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Calculate Royalties</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate Royalties" to see your earnings estimate</li>
                  <li>Enable detailed analysis for platform comparisons</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-blue-600">❓</span></h2>
            <div className="text-gray-600 space-y-4">
              <div>
                <h3 className="font-semibold">How are streaming royalties calculated?</h3>
                <p>Royalties are typically calculated per stream, with each platform having its own rate based on revenue and usage patterns.</p>
              </div>
              <div>
                <h3 className="font-semibold">Why do different platforms pay different rates?</h3>
                <p>Rates vary based on platform business models, subscription prices, advertising revenue, and market position.</p>
              </div>
              <div>
                <h3 className="font-semibold">Are these calculations exact?</h3>
                <p>These are estimates based on industry averages. Actual payments may vary based on contracts and platform-specific calculations.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">🔢</span></h2>
            <div className="text-gray-600 bg-gray-50 p-6 rounded-lg">
              <p><strong>Inputs:</strong></p>
              <ul className="list-disc pl-6">
                <li>Streams: 100,000</li>
                <li>Platform: Spotify</li>
              </ul>
              <p className="mt-4"><strong>Results:</strong></p>
              <ul className="list-disc pl-6">
                <li>Total Earnings: $330.00</li>
                <li>Earnings per 1000 Streams: $3.30</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoyaltyCalculator;